import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, Button, Space, Modal } from 'antd';
import {
  AppstoreOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  ApiOutlined,
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth';
import { useRegionStore } from '../../stores/region';
import { api } from '../../utils/request';
import logoImg from '../../assets/kimap-logo.png';
import logoSmallImg from '../../assets/kimap-logo-small.png';
import LanguageSwitcher from '../LanguageSwitcher';
import ProfileModal from '../ProfileModal';
import SupportTicketModal from '../SupportTicketModal';

const { Header, Sider, Content } = Layout;

export default function AppLayout({ children }: { children?: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [supportModalVisible, setSupportModalVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth, updateQuota, updateSubscription, unreadCount } = useAuthStore();
  const { fetchRegion } = useRegionStore();
  const { t } = useTranslation();

  // Collapse sidebar on SDK page (docs have their own sidebar)
  useEffect(() => {
    if (location.pathname === '/sdk') {
      setCollapsed(true);
    }
  }, [location.pathname]);
  useEffect(() => {
    const controller = new AbortController();
    const loadSubscriptionData = async () => {
      try {
        const data = await api.get<any>('/subscription/current', {
          signal: controller.signal,
        });
        if (data.subscription !== undefined || data.packageLevel !== undefined) {
          updateSubscription(data);
        }
        if (data.quota) {
          updateQuota(data.quota);
        }
      } catch (err: any) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Failed to load subscription data:', err);
        }
      }
    };
    loadSubscriptionData();
    api.get<{ count: number }>('/notifications/unread-count', { signal: controller.signal })
      .then(data => useAuthStore.getState().setUnreadCount(data?.count ?? 0))
      .catch(() => {});
    fetchRegion();
    return () => controller.abort();
  }, []);

  const menuItems = [
    { key: '/maps', icon: <AppstoreOutlined />, label: <Link to="/maps">{t('menu.onlineMaps')}</Link> },
    { key: '/subscription', icon: <CreditCardOutlined />, label: <Link to="/subscription">{t('menu.subscription')}</Link> },
    { key: '/billing', icon: <FileTextOutlined />, label: <Link to="/billing">{t('menu.billing')}</Link> },
    { key: '/sdk', icon: <ApiOutlined />, label: <Link to="/sdk">{t('menu.sdk')}</Link> },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: (
        <Link to="/notifications" className="flex items-center justify-between w-full">
          <span>{t('menu.notifications')}</span>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Link>
      ),
    },
    { key: '/support', icon: <QuestionCircleOutlined />, label: <Link to="/support">{t('menu.support')}</Link> },
  ];

  const selectedKeys = [location.pathname];

  const handleLogout = () => {
    Modal.confirm({
      title: t('menu.logoutConfirmTitle'),
      content: t('menu.logoutConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () => {
        clearAuth();
        navigate('/');
      },
    });
  };

  const userMenu = (
    <div className="bg-white rounded shadow-lg p-2 min-w-[200px]">
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="font-medium">{user?.realName || user?.username}</div>
        <div className="text-xs text-gray-500">{user?.email || ''}</div>
      </div>
      <div className="py-1">
        <button
          onClick={() => setProfileModalVisible(true)}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <UserOutlined /> {t('menu.profile')}
        </button>
        <button
          onClick={handleLogout}
          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2"
        >
          <LogoutOutlined /> {t('menu.logout')}
        </button>
      </div>
    </div>
  );

  return (
    <Layout className="h-screen overflow-hidden">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        className="!bg-white border-r border-gray-200 !h-screen"
      >
        <div className="h-12 flex items-center justify-center border-b border-gray-200">
          {collapsed ? (
            <img src={logoSmallImg} alt="Kimap" className="h-5 w-auto" />
          ) : (
            <img src={logoImg} alt="Kimap" className="h-6 w-auto" />
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          items={menuItems}
          className="!border-none"
        />
      </Sider>
      <Layout className="flex flex-col !h-screen">
        <Header className="px-4 flex items-center justify-between !bg-white !p-0 !h-12 !leading-[64px] border-b border-gray-200 flex-shrink-0" style={{ padding: '0 16px', background: '#fff' }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="!border-none !shadow-none"
          />
          <Space size={16}>
            <LanguageSwitcher />
            <Dropdown popupRender={() => userMenu} trigger={['click']}>
              <Button type="text" className="flex items-center gap-2 !border-none !shadow-none">
                <Avatar style={{ backgroundColor: '#1e5a8a' }}>
                  {(user?.realName || user?.username || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <span>{user?.realName || user?.username}</span>
              </Button>
            </Dropdown>
          </Space>
        </Header>
        <Content className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {children || <Outlet />}
        </Content>
      </Layout>
      <ProfileModal visible={profileModalVisible} onClose={() => setProfileModalVisible(false)} />
      <SupportTicketModal visible={supportModalVisible} onClose={() => setSupportModalVisible(false)} />
    </Layout>
  );
}
